import { PERMISSIONS } from "@/constants/permissions"
import { useGuardedMutation } from "@/hooks/useGuardedMutation"
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query"
import { useEffect, useMemo, useRef } from "react"
import { apiClientV2 } from "../../../services/api/apiClient"
import {
  fingerprintIds,
  mcCursorList,
  mcCursorListKeepPrevious,
  postIdsInChunks,
  useMcPatch,
} from "./mcQueryHelpers"
import type {
  ArticleComplianceOut,
  ArticleOut,
  ArticleRow,
  ArticleSubstanceCreateIn,
  ArticleSubstanceOut,
  ArticleSubstanceUpdateIn,
  ArticleUpdateIn,
  ComplianceEventOut,
  ProductArticleRow,
  SupplierArticleRow,
  SupplierArticleUpdateIn,
} from "./types/masterData"

const BASE = "/material-compliance/master-data/articles"

// ─── List ─────────────────────────────────────────────────────────────────────

export function useArticleList(
  params: {
    q?: string
    supplier_id?: string
    product_id?: string
    customer_id?: string
    regulation_id?: string
    substance_id?: string
    status?: string
    no_supplier?: boolean
    product_group?: string
    no_product_group?: boolean
    /** MC_MASTER_DATA_MANUFACTURER_FILTER only; the backend ignores both with the flag off. */
    manufacturer?: string
    no_manufacturer?: boolean
    /** With supplier_id: active links or the legacy column only (the set an inquiry is seeded from). */
    active_supplier_links?: boolean
    sort?: string
    limit?: number
    /** Set false to skip the request entirely, e.g. while a sibling query owns the active view. Defaults to true. */
    enabled?: boolean
  } = {},
) {
  const { enabled = true, ...listParams } = params
  return useInfiniteQuery(
    mcCursorListKeepPrevious<ArticleRow>(
      ["mc", "articles", "list", listParams],
      BASE,
      listParams,
      enabled,
    ),
  )
}

// ─── Detail ───────────────────────────────────────────────────────────────────

export function useArticleDetail(id: string | null) {
  return useQuery({
    queryKey: ["mc", "articles", "detail", id],
    queryFn: () =>
      apiClientV2.get<ArticleOut>(`${BASE}/${id}`).then((r) => r.data),
    enabled: !!id,
  })
}

// ─── Product group values (facet + bulk-assign picker) ─────────────────────────

export function useProductGroups() {
  return useQuery({
    queryKey: ["mc", "articles", "product-groups"],
    queryFn: () =>
      apiClientV2
        .get<{ value: string; count: number }[]>(`${BASE}/product-groups`)
        .then((r) => r.data),
    staleTime: 60 * 1000,
  })
}

// ─── Manufacturer values (facet, MC_MASTER_DATA_MANUFACTURER_FILTER) ───

/** Pass the flag as `enabled`: with it off the endpoint answers 403. */
export function useManufacturers(enabled: boolean) {
  return useQuery({
    queryKey: ["mc", "articles", "manufacturers"],
    queryFn: () =>
      apiClientV2
        .get<{ value: string; count: number }[]>(`${BASE}/manufacturers`)
        .then((r) => r.data),
    staleTime: 60 * 1000,
    enabled,
  })
}

// ─── Bulk-assign product group (across every filtered article, not a page) ─────

export interface ArticleBulkFilters {
  q?: string
  supplierId?: string
  productId?: string
  customerId?: string
  regulationId?: string
  substanceId?: string
  noSupplier?: boolean
  productGroup?: string
  noProductGroup?: boolean
  manufacturer?: string
  noManufacturer?: boolean
  status?: string[]
  /** Checkbox selection from the right-hand panel. An explicit `[]` means
   * "select nothing" (backend touches zero rows), distinct from `undefined`
   * ("no id filter"), so this must not be defaulted away. */
  articleIds?: string[]
}

function toBulkFiltersBody(filters: ArticleBulkFilters) {
  return {
    q: filters.q,
    supplier_id: filters.supplierId,
    product_id: filters.productId,
    customer_id: filters.customerId,
    regulation_id: filters.regulationId,
    substance_id: filters.substanceId,
    no_supplier: filters.noSupplier ?? false,
    product_group: filters.productGroup,
    no_product_group: filters.noProductGroup ?? false,
    // Left undefined (dropped from the JSON) unless set: the body stays what it
    // was for every caller that does not filter by manufacturer.
    manufacturer: filters.manufacturer,
    no_manufacturer: filters.noManufacturer,
    status: filters.status,
    article_ids: filters.articleIds,
  }
}

export function useBulkProductGroupPreview(
  filters: ArticleBulkFilters,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["mc", "articles", "bulk-product-group-preview", filters],
    queryFn: () =>
      apiClientV2
        .post<{ affectedCount: number }>(`${BASE}/bulk/product-group/preview`, {
          filters: toBulkFiltersBody(filters),
        })
        .then((r) => r.data.affectedCount),
    enabled,
  })
}

export function useBulkSetProductGroup() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({
      filters,
      productGroup,
    }: {
      filters: ArticleBulkFilters
      productGroup: string
    }) =>
      apiClientV2
        .post<{ updatedCount: number }>(`${BASE}/bulk/product-group`, {
          filters: toBulkFiltersBody(filters),
          product_group: productGroup,
        })
        .then((r) => r.data.updatedCount),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mc", "articles"] })
      qc.invalidateQueries({ queryKey: ["mc", "master-data", "stats"] })
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_MASTER_DATA_EDIT)
}

// ─── Selection resolve (checkbox selection → recipients / display rows) ───────

/** Master data does not move while a selection is being acted on, and a
 *  refetch would re-POST the whole selection — so a window refocus must not
 *  trigger one. staleTime alone only defers that by this long: react-query's
 *  default refetchOnWindowFocus refires once a query is stale, which is
 *  exactly a refocus after tabbing away for over a minute. The three
 *  selection queries also set refetchOnWindowFocus/refetchOnReconnect: false,
 *  which is what actually delivers the guarantee. */
export const SELECTION_STALE_TIME = 60_000

/** Must stay ≤ the backend's ARTICLE_SELECTION_MAX_IDS / ARTICLE_RESOLVE_MAX_IDS. */
export const ARTICLE_SELECTION_CHUNK = 5000
export const ARTICLE_RESOLVE_CHUNK = 1000

export interface ArticleSupplierGroup {
  supplierId: string
  supplierName: string
  /** The selected articles this supplier would be asked about. Ids only: the
   * display fields are resolved per visible window, not for the whole group. */
  articleIds: string[]
}

export interface ResolvedArticle {
  id: string
  articleNumber: string
  name: string
  productGroup: string | null
}

/**
 * Drop what a supplier-link write invalidates on the SELECTION surfaces: which
 * supplier an article is asked from, and the rows a recipient list shows. A
 * plain list invalidation misses these — their keys are fingerprints of an id
 * set, not list params, so nothing else refetches them.
 */
export function invalidateArticleSelectionCaches(qc: QueryClient): void {
  qc.invalidateQueries({ queryKey: ["mc", "articles", "by-supplier"] })
  qc.invalidateQueries({ queryKey: ["mc", "articles", "resolve"] })
  qc.invalidateQueries({ queryKey: ["mc", "articles", "count-matching"] })
}

/** Chunked responses repeat a supplier whenever its articles straddle a chunk
 * boundary — fold them into one group, keeping first-seen order. */
function mergeSupplierGroups(chunks: ArticleSupplierGroup[]) {
  const merged = new Map<string, ArticleSupplierGroup>()
  for (const group of chunks) {
    const existing = merged.get(group.supplierId)
    // Copy on insert: the merged group owns its array, so appending a later
    // chunk never writes into the response the query still holds.
    if (existing) existing.articleIds.push(...group.articleIds)
    else
      merged.set(group.supplierId, {
        ...group,
        articleIds: [...group.articleIds],
      })
  }
  return [...merged.values()]
}

/**
 * The selected articles grouped by the supplier that would be asked about
 * them, resolved server-side.
 *
 * Pass a MEMOIZED array: the query key is a fingerprint of the ids, and
 * computing it is one pass over the selection.
 */
export function useArticleSupplierGroups(articleIds: string[], enabled = true) {
  // Skipped while disabled, same as useResolveArticles: the sibling flow owns
  // the view, and hashing its whole selection for a query that never runs is
  // exactly the work the opt-out exists to avoid.
  const idsKey = useMemo(
    () => (enabled ? fingerprintIds(articleIds) : ""),
    [articleIds, enabled],
  )

  // The selection that was really resolved, and the ids its response covers.
  // Removing a recipient leaves a SUBSET of that, and the caller already
  // filters the kept response against the live selection — so the answer is
  // fully known and re-POSTing the remaining (possibly 15,000) ids would buy
  // nothing. Adding one is not a subset and does resolve again.
  const resolved = useRef<{
    key: string
    ids: Set<string>
    list: string[]
  } | null>(null)
  const isSubsetOfResolved = useMemo(
    () =>
      resolved.current !== null &&
      articleIds.every((id) => resolved.current!.ids.has(id)),
    [articleIds],
  )

  // While the selection stays inside what was resolved, keep READING that
  // selection's own cache entry rather than disabling a query on a key that
  // holds nothing: the shrink is still instant (same key, response already
  // there, no request), and the query stays active and ENABLED — which is what
  // lets a supplier-link write's invalidation refetch it. A disabled query is
  // only marked stale, so the panel would otherwise keep serving the grouping
  // from before the write (see invalidateArticleSelectionCaches).
  const pinned = isSubsetOfResolved ? resolved.current : null
  const activeKey = pinned?.key ?? idsKey
  // Every entry holds the response for the ids its key fingerprints, so a
  // refetch under a pinned key re-POSTs the RESOLVED ids, not the smaller live
  // selection. That keeps key and content in step; the caller narrows it down.
  const activeIds = pinned?.list ?? articleIds

  // eslint-disable-next-line @tanstack/query/exhaustive-deps -- activeKey IS the identity of activeIds
  const query = useQuery<ArticleSupplierGroup[]>({
    queryKey: ["mc", "articles", "by-supplier", activeKey],
    queryFn: ({ signal }) =>
      postIdsInChunks<ArticleSupplierGroup>(
        `${BASE}/by-supplier`,
        "articleIds",
        activeIds,
        ARTICLE_SELECTION_CHUNK,
        signal,
      ).then(mergeSupplierGroups),
    enabled: enabled && articleIds.length > 0,
    // A selection that GROWS changes this key. Without the kept previous
    // response the whole list would blank out to its loading state meanwhile;
    // the caller filters the kept response against the live selection (see
    // mcCursorListKeepPrevious for the same reason) and `isCovered` below is
    // what keeps it from being SENT.
    placeholderData: keepPreviousData,
    // The response scales with the selection, and structural sharing would
    // deep-compare every id against the previous result on each refetch.
    structuralSharing: false,
    staleTime: SELECTION_STALE_TIME,
    // See SELECTION_STALE_TIME: staleTime only defers a refocus refetch, it
    // doesn't prevent one.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

  useEffect(() => {
    // Only a settled response under this selection's OWN key says what is
    // resolved: a pinned key's response already recorded the set it covers,
    // and placeholder data belongs to the previous key.
    if (activeKey !== idsKey) return
    if (query.data === undefined || query.isPlaceholderData || query.isFetching)
      return
    resolved.current = {
      key: idsKey,
      ids: new Set(articleIds),
      list: articleIds,
    }
  }, [
    activeKey,
    idsKey,
    query.data,
    query.isPlaceholderData,
    query.isFetching,
    articleIds,
  ])

  // The endpoint groups only ids it was handed, so every article id in a
  // response must come from the list its key stands for. One that does not
  // means the entry was computed for a DIFFERENT selection, which is the shape
  // a query-key collision takes and the one way a recipient can go missing
  // with nothing on screen looking wrong. Refuse to call that covered: the
  // panel shows the resolve-error line and stays unsendable until the
  // selection changes and a fresh key resolves. (The reverse check, that every
  // SELECTED id is accounted for, is not available: the endpoint drops an
  // article with no supplier silently, so missing and supplier-less look the
  // same from here.) The ids come back as sent: `_dedupe_ids` in mc_schemas.py
  // strips, drops blanks and dedupes, and every id here is a row id from the
  // API. A padded one would fail this check, which is the right answer anyway,
  // because the panel's own filter would silently drop the stripped id.
  const answersItsOwnKey = useMemo(() => {
    if (query.data === undefined) return false
    const asked = new Set(activeIds)
    return query.data.every((group) =>
      group.articleIds.every((id) => asked.has(id)),
    )
  }, [query.data, activeIds])

  const settled =
    query.data !== undefined && !query.isPlaceholderData && !query.isFetching

  // Does the response at hand really answer for the CURRENT selection? Only a
  // settled, non-placeholder one does — a selection that grew, or one whose
  // entry a write has just invalidated, is served the PREVIOUS selection's
  // recipients, which is a real, non-empty list. A caller that sends on "not
  // empty" would drop the supplier only the added article has, so send on this.
  const isCovered = articleIds.length === 0 || (settled && answersItsOwnKey)

  // A settled response that still fails the coverage check above is not a
  // load in progress — it's a key collision (see answersItsOwnKey), so the
  // caller shouldn't say "still loading" about it.
  const answersOtherSelection =
    articleIds.length > 0 && settled && !answersItsOwnKey

  return { query, isCovered, answersOtherSelection }
}

/**
 * Ids → the few fields a row shows. Sized for one virtualized window, so keep
 * the array short and memoized; `enabled` is how a caller opts out for a
 * selection too large to display at all.
 */
export function useResolveArticles(articleIds: string[], enabled = true) {
  // Skipped while disabled: a caller opts out precisely because the selection
  // is large, and fingerprinting it anyway is the cost it opted out of.
  const idsKey = useMemo(
    () => (enabled ? fingerprintIds(articleIds) : ""),
    [articleIds, enabled],
  )
  // eslint-disable-next-line @tanstack/query/exhaustive-deps -- idsKey IS the identity of articleIds
  return useQuery({
    queryKey: ["mc", "articles", "resolve", idsKey],
    queryFn: ({ signal }) =>
      postIdsInChunks<ResolvedArticle>(
        `${BASE}/resolve`,
        "articleIds",
        articleIds,
        ARTICLE_RESOLVE_CHUNK,
        signal,
      ),
    enabled: enabled && articleIds.length > 0,
    staleTime: SELECTION_STALE_TIME,
    // See SELECTION_STALE_TIME: staleTime only defers a refocus refetch, it
    // doesn't prevent one.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })
}

/**
 * How many of `articleIds` match a search, with the same match GET /articles runs.
 * The inquiry "Add articles" dialog subtracts this from the list total for its
 * "Add all (n)", so the two can never disagree about which fields matched.
 */
export function useCountMatchingArticles(
  params: {
    articleIds: string[]
    q?: string
    supplierId: string
    productGroup?: string
    noProductGroup?: boolean
  },
  enabled = true,
) {
  const { articleIds, q, supplierId, productGroup, noProductGroup } = params
  const idsKey = useMemo(
    () => (enabled ? fingerprintIds(articleIds) : ""),
    [articleIds, enabled],
  )
  // eslint-disable-next-line @tanstack/query/exhaustive-deps -- idsKey IS the identity of articleIds
  return useQuery({
    queryKey: [
      "mc",
      "articles",
      "count-matching",
      idsKey,
      { q, supplierId, productGroup, noProductGroup },
    ],
    queryFn: ({ signal }) =>
      apiClientV2
        .post<{ count: number }>(
          `${BASE}/count-matching`,
          {
            articleIds,
            q,
            supplierId,
            activeSupplierLinks: true,
            productGroup,
            noProductGroup,
          },
          { signal },
        )
        .then((r) => r.data.count),
    enabled: enabled && articleIds.length > 0,
  })
}

// ─── Sub-resource: substances in this article ─────────────────────────────────

export function useArticleSubstances(id: string | null) {
  return useQuery({
    queryKey: ["mc", "articles", id, "substances"],
    queryFn: () =>
      apiClientV2
        .get<ArticleSubstanceOut[]>(`${BASE}/${id}/substances`)
        .then((r) => r.data),
    enabled: !!id,
  })
}

export function useUpdateArticleSubstance() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({
      articleId,
      linkId,
      changes,
    }: {
      articleId: string
      linkId: string
      changes: ArticleSubstanceUpdateIn
    }) =>
      apiClientV2
        .patch<ArticleSubstanceOut>(
          `${BASE}/${articleId}/substances/${linkId}`,
          changes,
        )
        .then((r) => r.data),
    onSuccess: (_, { articleId }) => {
      qc.invalidateQueries({
        queryKey: ["mc", "articles", articleId, "substances"],
      })
      qc.invalidateQueries({
        queryKey: ["mc", "articles", articleId, "audit"],
      })
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_MASTER_DATA_EDIT)
}

// ─── Sub-resource: per-supplier × regulation compliance verdicts ──────────────

export function useArticleCompliance(id: string | null) {
  return useQuery({
    queryKey: ["mc", "articles", id, "compliance"],
    queryFn: () =>
      apiClientV2
        .get<ArticleComplianceOut[]>(`${BASE}/${id}/compliance`)
        .then((r) => r.data),
    enabled: !!id,
  })
}

export function useUpdateArticleCompliance() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({
      articleId,
      complianceId,
      changes,
    }: {
      articleId: string
      complianceId: string
      changes: {
        status: string
        exemptionCode?: string | null
        source?: string
      }
    }) =>
      apiClientV2
        .put<ArticleComplianceOut>(
          `${BASE}/${articleId}/compliance/${complianceId}`,
          changes,
        )
        .then((r) => r.data),
    onSuccess: (_, { articleId }) => {
      qc.invalidateQueries({
        queryKey: ["mc", "articles", articleId, "compliance"],
      })
      qc.invalidateQueries({
        queryKey: ["mc", "articles", articleId, "audit"],
      })
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_MASTER_DATA_EDIT)
}

// ─── Sub-resource: child articles (sub-assembly BOM) ──────────────────────────

export function useArticleChildren(
  id: string | null,
  params: { limit?: number } = {},
) {
  return useInfiniteQuery(
    mcCursorList<ProductArticleRow>(
      ["mc", "articles", id, "children", params],
      `${BASE}/${id}/children`,
      params,
      !!id,
    ),
  )
}

// ─── Sub-resource: audit trail ────────────────────────────────────────────────

export function useArticleAudit(
  id: string | null,
  params: { limit?: number } = {},
) {
  return useInfiniteQuery(
    mcCursorList<ComplianceEventOut>(
      ["mc", "articles", id, "audit", params],
      `${BASE}/${id}/audit`,
      params,
      !!id,
    ),
  )
}

// ─── Sub-resource: supplier ↔ article n:m ────────────────────────────────────

export function useArticleSuppliers(id: string | null) {
  return useQuery({
    queryKey: ["mc", "articles", id, "suppliers"],
    queryFn: () =>
      apiClientV2
        .get<SupplierArticleRow[]>(`${BASE}/${id}/suppliers`)
        .then((r) => r.data),
    enabled: !!id,
  })
}

export function useAddArticleSupplier() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({
      articleId,
      supplierId,
      supplierArticleNumber,
    }: {
      articleId: string
      supplierId: string
      supplierArticleNumber?: string | null
    }) =>
      apiClientV2
        .post<SupplierArticleRow>(`${BASE}/${articleId}/suppliers`, {
          supplier_id: supplierId,
          supplier_article_number: supplierArticleNumber ?? null,
        })
        .then((r) => r.data),
    onSuccess: (_, { articleId }) => {
      qc.invalidateQueries({
        queryKey: ["mc", "articles", "detail", articleId],
      })
      qc.invalidateQueries({
        queryKey: ["mc", "articles", articleId, "suppliers"],
      })
      qc.invalidateQueries({
        queryKey: ["mc", "articles", articleId, "audit"],
      })
      qc.invalidateQueries({ queryKey: ["mc", "articles", "list"] })
      invalidateArticleSelectionCaches(qc)
      // A link write moves rows on the SUPPLIER's surfaces too (its article
      // list gains this part) and the shared master-data stats — mirrors
      // invalidateLinkCaches / invalidateMcEntityCaches in masterDataWriteApi.ts.
      qc.invalidateQueries({ queryKey: ["mc", "suppliers"] })
      qc.invalidateQueries({ queryKey: ["mc", "master-data", "stats"] })
      // Same reasoning for the "Datenlücken" tile (TUR-1539): linking a
      // supplier can move the article out of the "no supplier" gap bucket.
      qc.invalidateQueries({ queryKey: ["mc", "master-data", "data-gaps"] })
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_MASTER_DATA_EDIT)
}

export function useUpdateArticleSupplier() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({
      articleId,
      supplierId,
      changes,
    }: {
      articleId: string
      supplierId: string
      changes: SupplierArticleUpdateIn
    }) =>
      apiClientV2
        .patch<SupplierArticleRow>(
          `${BASE}/${articleId}/suppliers/${supplierId}`,
          changes,
        )
        .then((r) => r.data),
    onSuccess: (_, { articleId }) => {
      qc.invalidateQueries({
        queryKey: ["mc", "articles", "detail", articleId],
      })
      qc.invalidateQueries({
        queryKey: ["mc", "articles", articleId, "suppliers"],
      })
      qc.invalidateQueries({
        queryKey: ["mc", "articles", articleId, "audit"],
      })
      qc.invalidateQueries({ queryKey: ["mc", "articles", "list"] })
      invalidateArticleSelectionCaches(qc)
      qc.invalidateQueries({ queryKey: ["mc", "master-data", "data-gaps"] })
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_MASTER_DATA_EDIT)
}

export function useRemoveArticleSupplier() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({
      articleId,
      supplierId,
    }: {
      articleId: string
      supplierId: string
    }) =>
      apiClientV2
        .delete(`${BASE}/${articleId}/suppliers/${supplierId}`)
        .then((r) => r.data),
    onSuccess: (_, { articleId }) => {
      qc.invalidateQueries({
        queryKey: ["mc", "articles", "detail", articleId],
      })
      qc.invalidateQueries({
        queryKey: ["mc", "articles", articleId, "suppliers"],
      })
      qc.invalidateQueries({
        queryKey: ["mc", "articles", articleId, "audit"],
      })
      qc.invalidateQueries({ queryKey: ["mc", "articles", "list"] })
      invalidateArticleSelectionCaches(qc)
      // Removing a supplier link can push the article INTO the "no supplier"
      // gap bucket, same as the add/update paths above.
      qc.invalidateQueries({ queryKey: ["mc", "master-data", "data-gaps"] })
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_MASTER_DATA_EDIT)
}

// ─── Sub-resource: substance ↔ article n:m (write) ────────────────────────────

/**
 * Both mutations below invalidate more than `useAddArticleSupplier` /
 * `useRemoveArticleSupplier` do: linking or unlinking a SUBSTANCE moves rows
 * on the substance's own surfaces too (its used-in-articles count/list), not
 * just the article's — unlike a supplier link, which is article/supplier-only.
 * Mirrors `invalidateLinkCaches`'s "both sides move" doc comment in
 * masterDataWriteApi.ts, the non-React variant of this same write.
 */
export function useAddArticleSubstance() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({
      articleId,
      substanceId,
      supplierId,
      concentrationPct,
      exemption,
      exemptionCode,
    }: { articleId: string } & ArticleSubstanceCreateIn) =>
      apiClientV2
        .post<ArticleSubstanceOut>(`${BASE}/${articleId}/substances`, {
          substance_id: substanceId,
          supplier_id: supplierId,
          concentration_pct: concentrationPct ?? null,
          // Backend's ArticleSubstanceCreateIn.exemption is `bool = False`, not
          // Optional[bool] — sending null 422s. Default to false, not null.
          exemption: exemption ?? false,
          exemption_code: exemptionCode ?? null,
        })
        .then((r) => r.data),
    onSuccess: (_, { articleId, substanceId }) => {
      qc.invalidateQueries({
        queryKey: ["mc", "articles", "detail", articleId],
      })
      qc.invalidateQueries({
        queryKey: ["mc", "articles", articleId, "substances"],
      })
      qc.invalidateQueries({
        queryKey: ["mc", "articles", articleId, "audit"],
      })
      qc.invalidateQueries({ queryKey: ["mc", "articles", "list"] })
      // Substance side: the shared prefix covers both the org-scoped
      // ["mc","substances","list",...] and the unscoped catalog
      // ["mc","substances","global","list",...], plus this specific
      // substance's own detail/articles sub-resource, reachable here via the
      // request body's substanceId.
      qc.invalidateQueries({ queryKey: ["mc", "substances"] })
      qc.invalidateQueries({
        queryKey: ["mc", "substances", "detail", substanceId],
      })
      qc.invalidateQueries({
        queryKey: ["mc", "substances", substanceId, "articles"],
      })
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_MASTER_DATA_EDIT)
}

export function useRemoveArticleSubstance() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({
      articleId,
      linkId,
    }: {
      articleId: string
      linkId: string
    }) =>
      apiClientV2
        .delete(`${BASE}/${articleId}/substances/${linkId}`)
        .then((r) => r.data),
    onSuccess: (_, { articleId }) => {
      qc.invalidateQueries({
        queryKey: ["mc", "articles", "detail", articleId],
      })
      qc.invalidateQueries({
        queryKey: ["mc", "articles", articleId, "substances"],
      })
      qc.invalidateQueries({
        queryKey: ["mc", "articles", articleId, "audit"],
      })
      qc.invalidateQueries({ queryKey: ["mc", "articles", "list"] })
      // Only linkId/articleId are in scope here, not the unlinked substance's
      // own id — so the substance-side invalidation is limited to the shared
      // prefix, which still covers both the org-scoped and global-catalog
      // substance lists (see useAddArticleSubstance above for the id-scoped
      // case this mutation can't reach).
      qc.invalidateQueries({ queryKey: ["mc", "substances"] })
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_MASTER_DATA_EDIT)
}

// ─── Mutation ─────────────────────────────────────────────────────────────────

export function usePatchArticle() {
  return useMcPatch<ArticleOut, ArticleUpdateIn>("articles", BASE)
}
